import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Send, CheckCircle2 } from "lucide-react";

const VERTICALS = ["Gimnasio / CrossFit", "Escuela / Academia", "Clínica / Estética", "Barbería / Salón", "Residencial / Condominio", "Otro"];
const VOLUMES = ["Menos de RD$100,000/mes", "RD$100,000 - RD$500,000/mes", "RD$500,000 - RD$2MM/mes", "Más de RD$2MM/mes", "No estoy seguro"];

export default function DemoPage() {
  const [form, setForm] = useState({ name: "", business: "", contact: "", vertical: "", volume: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "¿Cuál es tu nombre?";
    if (!form.business.trim()) e.business = "¿Cuál es el nombre de tu negocio?";
    if (!form.contact.trim()) e.contact = "Necesitamos un email o WhatsApp para contactarte";
    if (!form.vertical) e.vertical = "Selecciona tu tipo de negocio";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const apiUrl = process.env.REACT_APP_DEMO_API_URL || "/api/demo";
      await fetch(apiUrl, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, timestamp: new Date().toISOString(), source: "landing-demo" }),
      });
    } catch {}
    setSending(false);
    setSubmitted(true);
    if (window.posthog) window.posthog.capture("lead_submit", { vertical: form.vertical, source: "landing-demo" });
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-[#F2EDE3] text-[#111111] font-['Satoshi'] flex items-center justify-center">
        <div className="max-w-lg mx-auto px-5 sm:px-8 text-center py-20">
          <CheckCircle2 className="w-16 h-16 text-[#075a22] mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-['Cabinet_Grotesk'] font-black text-4xl md:text-5xl text-[#075a22] mb-4">
            Recibimos tu solicitud
          </h1>
          <p className="text-xl text-[#111111]/85 leading-relaxed mb-8">
            Te contactamos en las próximas horas por WhatsApp o email para darte acceso y resolver tus dudas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`https://wa.me/18299278886?text=Hola%2C+quiero+una+demo+de+Kubo+Payments+%28${encodeURIComponent(form.business)}%29`}
               target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 bg-[#075a22] hover:bg-[#05471a] text-white text-lg font-bold px-8 py-5 rounded-full transition-all">
              Hablar por WhatsApp <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />
            </a>
            <Link to="/"
               className="inline-flex items-center gap-2 bg-[#111111] hover:bg-[#2a2a2a] text-white text-lg font-bold px-8 py-5 rounded-full transition-all">
              Volver a la landing
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F2EDE3] text-[#111111] font-['Satoshi']">
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16 md:py-24">
        <Link to="/"
          className="inline-flex items-center gap-2 text-sm font-bold text-[#a68a6d] hover:text-[#8f7659] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
          Volver
        </Link>

        <h1 className="font-['Cabinet_Grotesk'] font-black tracking-tighter leading-[0.95] text-[#075a22] text-4xl sm:text-5xl md:text-6xl mb-4">
          ¿Listo para cobrar en automático?
        </h1>
        <p className="text-xl text-[#111111]/85 mb-10">
          Cuéntanos de tu negocio y te mostramos cómo Kubo Payments resuelve tus cobros.
        </p>

        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          <div>
            <label className="block text-sm font-bold uppercase tracking-[0.12em] text-[#111111] mb-2">Tu nombre</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={`w-full px-5 py-4 rounded-2xl border bg-white text-lg transition-colors ${errors.name ? "border-red-400" : "border-[#111111]/10 focus:border-[#a68a6d]"}`}
              placeholder="Ej: Juan Pérez" />
            {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-bold uppercase tracking-[0.12em] text-[#111111] mb-2">Nombre del negocio</label>
            <input type="text" value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })}
              className={`w-full px-5 py-4 rounded-2xl border bg-white text-lg transition-colors ${errors.business ? "border-red-400" : "border-[#111111]/10 focus:border-[#a68a6d]"}`}
              placeholder="Ej: CrossFit Santo Domingo" />
            {errors.business && <p className="mt-1 text-sm text-red-500">{errors.business}</p>}
          </div>

          <div>
            <label className="block text-sm font-bold uppercase tracking-[0.12em] text-[#111111] mb-2">Email o WhatsApp</label>
            <input type="text" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
              className={`w-full px-5 py-4 rounded-2xl border bg-white text-lg transition-colors ${errors.contact ? "border-red-400" : "border-[#111111]/10 focus:border-[#a68a6d]"}`}
              placeholder="Ej: juan@ejemplo.com o 809-555-5555" />
            {errors.contact && <p className="mt-1 text-sm text-red-500">{errors.contact}</p>}
          </div>

          <div>
            <label className="block text-sm font-bold uppercase tracking-[0.12em] text-[#111111] mb-2">Tipo de negocio</label>
            <select value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })}
              className={`w-full px-5 py-4 rounded-2xl border bg-white text-lg transition-colors ${errors.vertical ? "border-red-400" : "border-[#111111]/10 focus:border-[#a68a6d]"}`}>
              <option value="">Selecciona una opción</option>
              {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            {errors.vertical && <p className="mt-1 text-sm text-red-500">{errors.vertical}</p>}
          </div>

          <div>
            <label className="block text-sm font-bold uppercase tracking-[0.12em] text-[#111111] mb-2">Volumen mensual estimado</label>
            <select value={form.volume} onChange={(e) => setForm({ ...form, volume: e.target.value })}
              className="w-full px-5 py-4 rounded-2xl border border-[#111111]/10 bg-white text-lg transition-colors focus:border-[#a68a6d]">
              <option value="">Selecciona un rango</option>
              {VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          <button type="submit" disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#a68a6d] hover:bg-[#8f7659] text-white text-lg font-bold px-8 py-5 rounded-full transition-all hover:scale-[1.02] shadow-[0_16px_40px_rgba(166,138,109,0.3)] disabled:opacity-50">
            {sending ? "Enviando..." : "Solicitar demo"}
            <Send className="w-5 h-5" strokeWidth={2.5} />
          </button>

          <p className="text-center text-sm text-[#111111]/60">
            También puedes escribirnos directo por{" "}
            <a href="https://wa.me/18299278886" target="_blank" rel="noopener noreferrer"
              className="font-bold text-[#075a22] underline underline-offset-2">WhatsApp</a>.
          </p>
          
          <p className="text-center text-xs text-[#111111]/50 mt-4">
            Tus datos solo se usan para contactarte. No los vendemos.
          </p>
        </form>
      </div>
    </main>
  );
}